import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, CardActionArea,
  Typography, CircularProgress, Avatar, Chip,
  IconButton, Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Balance as BalanceIcon,
  Gavel as GavelIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminListarServidores, adminListarDocumentos, clearAdminToken } from '../../services/adminApi';

const AdvogadoHomePage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const adminUser = (() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || '{}'); }
    catch { return {}; }
  })();

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const [resRegulares, resOficio, resDocs] = await Promise.all([
        adminListarServidores({ page: 1, limit: 1, search: '', oficio: false }),
        adminListarServidores({ page: 1, limit: 1, search: '', oficio: true }),
        adminListarDocumentos({ page: 1, limit: 1 }),
      ]);

      const totalRegulares = Array.isArray(resRegulares)
        ? resRegulares.length
        : resRegulares.total ?? 0;

      const totalOficio = Array.isArray(resOficio)
        ? resOficio.length
        : resOficio.total ?? 0;

      const totaisDoc = Array.isArray(resDocs)
        ? { assinados: resDocs.filter((d: any) => d.status === 'ASSINADO').length }
        : { assinados: resDocs.totais?.assinados ?? 0 };

      setStats({
        total_servidores_regulares: totalRegulares,
        total_servidores_oficio: totalOficio,
        documentos_assinados: totaisDoc.assinados,
      });
    } catch {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin/login');
  };

  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';

  const cards = [
    {
      label: 'Servidores Regulares',
      value: stats?.total_servidores_regulares ?? '—',
      description: 'Total de servidores regulares disponíveis para consulta',
      icon: <PeopleIcon sx={{ fontSize: 34 }} />,
      color: '#1565C0',
      bgGrad: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
      route: '/admin/advogado/servidores',
      actionLabel: 'Ver regulares',
    },
    {
      label: 'Servidores do Ofício',
      value: stats?.total_servidores_oficio ?? '—',
      description: 'Total de servidores do Ofício da Seduc disponíveis para consulta',
      icon: <PeopleIcon sx={{ fontSize: 34 }} />,
      color: '#7B1FA2',
      bgGrad: 'linear-gradient(135deg, #7B1FA2 0%, #4A148C 100%)',
      route: '/admin/advogado/servidores/oficio',
      actionLabel: 'Ver ofício',
    },
    {
      label: 'Documentos Assinados',
      value: stats?.documentos_assinados ?? '—',
      description: 'Documentos prontos para download',
      icon: <CheckCircleIcon sx={{ fontSize: 34 }} />,
      color: '#2E7D32',
      bgGrad: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
      route: '/admin/advogado/documentos',
      actionLabel: 'Ver documentos',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6FB' }}>

      {/* ── Top bar ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
        px: { xs: 3, md: 5 }, py: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 4px 24px rgba(13,71,161,0.28)',
      }}>
        {/* Left */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.15)',
            border: '1.5px solid rgba(255,255,255,0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BalanceIcon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} color="white"
              sx={{ lineHeight: 1.1, letterSpacing: '-0.3px' }}>
              Portal do Advogado
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 0.3 }}>
              SINTET — Acesso de consulta
            </Typography>
          </Box>
        </Box>

        {/* Right */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            icon={<GavelIcon sx={{ fontSize: '14px !important', color: 'rgba(255,255,255,0.8) !important' }} />}
            label="Advogado"
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.9)',
              border: '1px solid rgba(255,255,255,0.25)',
              fontWeight: 700, fontSize: '0.7rem',
              display: { xs: 'none', sm: 'flex' },
            }}
          />
          <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" fontWeight={700} color="white" sx={{ lineHeight: 1.2 }}>
              {adminUser.nome || adminUser.username}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              {adminUser.email || ''}
            </Typography>
          </Box>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.22)',
            width: 40, height: 40, fontSize: 16, fontWeight: 800,
            border: '2px solid rgba(255,255,255,0.35)', color: 'white',
          }}>
            {(adminUser.nome || adminUser.username || 'A').charAt(0).toUpperCase()}
          </Avatar>

          {/* ── Botão Logout ── */}
          <Tooltip title="Sair">
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 2,
                p: 0.75,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                },
              }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: { xs: 3, md: 6 }, py: 5, maxWidth: 1100, mx: 'auto' }}>

        {/* Welcome */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h4" fontWeight={800}
            sx={{ color: '#0D47A1', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
            {saudacao}, {adminUser.nome?.split(' ')[0] || adminUser.username} 👋
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
            Painel de consulta — visualize servidores e baixe documentos assinados.
          </Typography>
        </Box>

        {/* ── Stat Cards ── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={44} sx={{ color: '#1565C0' }} />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {cards.map((card, i) => (
              <Grid item xs={12} sm={6} key={card.label}>
                <Card
                  onClick={() => navigate(card.route)}
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    border: '1.5px solid', borderColor: `${card.color}22`,
                    cursor: 'pointer', overflow: 'hidden',
                    transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                    boxShadow: `0 4px 20px ${card.color}12`,
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 12px 36px ${card.color}28`,
                    },
                    animationDelay: `${i * 80}ms`,
                  }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Box sx={{
                      background: card.bgGrad,
                      px: 3, pt: 3, pb: 2.5,
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    }}>
                      <Box>
                        <Typography variant="caption" sx={{
                          color: 'rgba(255,255,255,0.72)', fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.67rem',
                        }}>
                          {card.label}
                        </Typography>
                        <Typography variant="h2" fontWeight={900} sx={{
                          color: 'white', lineHeight: 1, mt: 0.5,
                          fontSize: { xs: '2.8rem', md: '3.2rem' }, letterSpacing: '-1px',
                        }}>
                          {card.value}
                        </Typography>
                      </Box>
                      <Avatar sx={{
                        bgcolor: 'rgba(255,255,255,0.18)', width: 56, height: 56,
                        border: '1.5px solid rgba(255,255,255,0.28)', color: 'white',
                      }}>
                        {card.icon}
                      </Avatar>
                    </Box>
                    <Box sx={{
                      px: 3, py: 2, bgcolor: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {card.description}
                      </Typography>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        whiteSpace: 'nowrap', ml: 2,
                      }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: card.color }}>
                          {card.actionLabel}
                        </Typography>
                        <ArrowForwardIcon sx={{ fontSize: 15, color: card.color }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ── Quick access ── */}
        <Typography variant="overline" sx={{
          color: 'text.disabled', fontWeight: 700, letterSpacing: 2,
          fontSize: '0.68rem', mb: 2, display: 'block',
        }}>
          Acesso Rápido
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              label: 'Servidores Regulares',
              desc: 'Consulte dados cadastrais dos servidores regulares.',
              icon: <PeopleIcon sx={{ fontSize: 22 }} />,
              color: '#1565C0', bg: '#E3F2FD',
              route: '/admin/advogado/servidores',
            },
            {
              label: 'Servidores do Ofício',
              desc: 'Consulte os dados importados do Ofício da Seduc.',
              icon: <PeopleIcon sx={{ fontSize: 22 }} />,
              color: '#7B1FA2', bg: '#F3E5F5',
              route: '/admin/advogado/servidores/oficio',
            },
            {
              label: 'Documentos Assinados',
              desc: 'Visualize e baixe os PDFs já assinados.',
              icon: <CheckCircleIcon sx={{ fontSize: 22 }} />,
              color: '#2E7D32', bg: '#E8F5E9',
              route: '/admin/advogado/documentos',
            },
          ].map((action) => (
            <Grid item xs={12} sm={6} key={action.label}>
              <Card elevation={0} onClick={() => navigate(action.route)} sx={{
                borderRadius: 3, border: '1.5px solid', borderColor: 'divider',
                cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:hover': {
                  borderColor: action.color,
                  boxShadow: `0 4px 16px ${action.color}18`,
                  '& .qa-arrow': { transform: 'translateX(4px)' },
                },
              }}>
                <CardActionArea sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{
                    bgcolor: action.bg, color: action.color,
                    width: 44, height: 44, borderRadius: 2, flexShrink: 0,
                  }}>
                    {action.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{action.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{action.desc}</Typography>
                  </Box>
                  <ArrowForwardIcon className="qa-arrow" sx={{
                    fontSize: 18, color: action.color,
                    transition: 'transform 0.2s', flexShrink: 0,
                  }} />
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Read-only notice ── */}
        <Box sx={{
          mt: 5, px: 3, py: 2, borderRadius: 3,
          bgcolor: '#FFF8E1', border: '1.5px solid #FFE08222',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <GavelIcon sx={{ color: '#F57F17', fontSize: 20, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#5D4037', fontWeight: 500 }}>
            Acesso somente leitura — você pode visualizar dados e baixar documentos assinados, mas não pode editar ou excluir informações.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AdvogadoHomePage;